import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { isSafari, getOAuthProcessingDelay } from '@/utils/browser-detection';
import { timeouts } from '@/lib/environment';

interface OAuthState {
  isProcessing: boolean;
  processingSessionId: string | null;
  lastProcessedSessionId: string | null;
  lastProcessedTimestamp: number | null;
  processedEvents: Set<string>; // Track which events have been processed for a session
  error: string | null;
  provider: string | null;
  isSafariBrowser: boolean; // Track if we're in Safari
  processingStartTime: number | null; // Track when processing started
}

interface OAuthContextType {
  oauthState: OAuthState;
  startOAuthProcessing: (sessionId: string, provider: string, eventType?: string) => boolean;
  completeOAuthProcessing: () => void;
  setOAuthError: (error: string | null) => void;
  isOAuthRedirect: () => boolean;
  getProcessingSessionId: () => string | null;
  hasProcessedEvent: (sessionId: string, eventType: string) => boolean;
  isWithinGracePeriod: () => boolean;
}

const OAuthContext = createContext<OAuthContextType | undefined>(undefined);

export function OAuthProvider({ children }: { children: ReactNode }) {
  const [oauthState, setOAuthState] = useState<OAuthState>({
    isProcessing: false,
    processingSessionId: null,
    lastProcessedSessionId: null,
    lastProcessedTimestamp: null,
    processedEvents: new Set(),
    error: null,
    provider: null,
    isSafariBrowser: false,
    processingStartTime: null
  });

  // Detect Safari on mount
  useEffect(() => {
    const safari = isSafari();
    if (safari) {
      console.log('[OAuthContext] Safari browser detected - applying special handling');
      setOAuthState(prev => ({ ...prev, isSafariBrowser: true }));
    }
  }, []);

  // Removed auto-clear effect that was causing render loops

  // Check if current URL indicates OAuth redirect
  const isOAuthRedirect = useCallback(() => {
    // Check URL params for OAuth code
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = !!urlParams.get('code');
    
    // Check hash params for tokens
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const hasTokens = !!(hashParams.get('access_token') || hashParams.get('refresh_token'));
    
    // For Safari, also check if we're within the processing window
    if (oauthState.isSafariBrowser && oauthState.processingStartTime) {
      const timeSinceStart = Date.now() - oauthState.processingStartTime;
      const isWithinSafariWindow = timeSinceStart < (getOAuthProcessingDelay() * 2); // Double the delay for safety
      return hasCode || hasTokens || oauthState.isProcessing || isWithinSafariWindow;
    }
    
    return hasCode || hasTokens || oauthState.isProcessing;
  }, [oauthState.isProcessing, oauthState.isSafariBrowser, oauthState.processingStartTime]);

  const startOAuthProcessing = useCallback((sessionId: string, provider: string, eventType?: string) => {
    // Check if we recently processed this session (within last 5 seconds, or 10 seconds for Safari)
    const now = Date.now();
    const recentThreshold = oauthState.isSafariBrowser ? 10000 : 5000; // Safari needs longer threshold
    
    if (oauthState.lastProcessedSessionId === sessionId && 
        oauthState.lastProcessedTimestamp && 
        (now - oauthState.lastProcessedTimestamp) < recentThreshold) {
      console.log(`[OAuthContext] Recently processed session ${sessionId}, skipping`);
      return false; // Indicate we didn't start processing
    }
    
    // Check if we've already processed this specific event for this session
    const eventKey = `${sessionId}:${eventType || 'unknown'}`;
    if (oauthState.processedEvents.has(eventKey)) {
      console.log(`[OAuthContext] Already processed event ${eventType} for session ${sessionId}, skipping`);
      return false;
    }
    
    console.log(`[OAuthContext] Starting OAuth processing for ${provider}, event: ${eventType}`, sessionId);
    console.log(`[OAuthContext] Browser: ${oauthState.isSafariBrowser ? 'Safari' : 'Other'}`);
    
    setOAuthState(prev => ({
      ...prev,
      isProcessing: true,
      processingSessionId: sessionId,
      processedEvents: new Set([...prev.processedEvents, eventKey]),
      error: null,
      provider,
      processingStartTime: Date.now()
    }));
    
    // For Safari, add an additional delay to ensure storage operations complete
    if (oauthState.isSafariBrowser) {
      const delay = getOAuthProcessingDelay();
      console.log(`[OAuthContext] Safari detected - adding ${delay}ms delay for storage operations`);
      setTimeout(() => {
        // This delay ensures Safari has time to persist storage operations
      }, delay);
    }
    
    return true; // Indicate we started processing
  }, [oauthState.lastProcessedSessionId, oauthState.lastProcessedTimestamp, oauthState.processedEvents, oauthState.isSafariBrowser]);

  const completeOAuthProcessing = useCallback(() => {
    console.log('[OAuthContext] Completing OAuth processing');
    
    setOAuthState(prev => {
      const processingDuration = prev.processingStartTime ? Date.now() - prev.processingStartTime : 0;
      console.log(`[OAuthContext] Processing duration: ${processingDuration}ms`);
      
      // For Safari, ensure minimum processing time to avoid race conditions
      if (prev.isSafariBrowser && processingDuration < getOAuthProcessingDelay()) {
        const remainingDelay = getOAuthProcessingDelay() - processingDuration;
        console.log(`[OAuthContext] Safari: Adding ${remainingDelay}ms delay before completing`);
        
        setTimeout(() => {
          setOAuthState(current => ({
            ...current,
            isProcessing: false,
            lastProcessedSessionId: current.processingSessionId,
            lastProcessedTimestamp: Date.now(),
            processingSessionId: null,
            processingStartTime: null
          }));
        }, remainingDelay);
        
        return prev; // Don't update yet
      }
      
      return {
        ...prev,
        isProcessing: false,
        lastProcessedSessionId: prev.processingSessionId,
        lastProcessedTimestamp: Date.now(),
        processingSessionId: null,
        processingStartTime: null
      };
    });
  }, []);

  const setOAuthError = useCallback((error: string | null) => {
    console.log('[OAuthContext] Setting OAuth error:', error);
    setOAuthState(prev => ({
      ...prev,
      error,
      isProcessing: false,
      processingSessionId: null
    }));
  }, []);

  const getProcessingSessionId = useCallback(() => {
    return oauthState.processingSessionId;
  }, [oauthState.processingSessionId]);

  const hasProcessedEvent = useCallback((sessionId: string, eventType: string) => {
    const eventKey = `${sessionId}:${eventType}`;
    return oauthState.processedEvents.has(eventKey);
  }, [oauthState.processedEvents]);

  // Check if we're within the grace period after OAuth completion
  const isWithinGracePeriod = useCallback(() => {
    // If currently processing, we're definitely in grace period
    if (oauthState.isProcessing) return true;
    
    // If never processed, not in grace period
    if (!oauthState.lastProcessedTimestamp) return false;
    
    // Check if we're within the grace period after completion
    const now = Date.now();
    // Use environment-aware grace period - shorter in UAT to reduce wait time
    const gracePeriod = oauthState.isSafariBrowser ? timeouts.oauthGracePeriod * 2 : timeouts.oauthGracePeriod;
    const timeSinceCompletion = now - oauthState.lastProcessedTimestamp;
    
    // Don't update state during render - just return false if past grace period
    if (timeSinceCompletion > 30000) {
      return false;
    }
    
    return timeSinceCompletion < gracePeriod;
  }, [oauthState.isProcessing, oauthState.lastProcessedTimestamp, oauthState.isSafariBrowser]);

  return (
    <OAuthContext.Provider
      value={{
        oauthState,
        startOAuthProcessing,
        completeOAuthProcessing,
        setOAuthError,
        isOAuthRedirect,
        getProcessingSessionId,
        hasProcessedEvent,
        isWithinGracePeriod
      }}
    >
      {children}
    </OAuthContext.Provider>
  );
}

export function useOAuth() {
  const context = useContext(OAuthContext);
  if (!context) {
    throw new Error('useOAuth must be used within OAuthProvider');
  }
  return context;
}